import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX, RotateCcw } from 'lucide-react';
import { generateVideoThumbnail, generateVideoThumbnails, cleanupThumbnails } from '../../utils/video-thumbnail';

interface VideoPreviewProps {
  file: File;
  onThumbnailGenerated?: (thumbnail: string) => void;
  className?: string;
}

export const VideoPreview: React.FC<VideoPreviewProps> = ({
  file,
  onThumbnailGenerated,
  className = ''
}) => {
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [thumbnail, setThumbnail] = useState<string>('');
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // Create video URL for preview
    const url = URL.createObjectURL(file);
    setVideoUrl(url);

    // Generate main thumbnail
    generateMainThumbnail();

    return () => {
      URL.revokeObjectURL(url);
      if (thumbnail) URL.revokeObjectURL(thumbnail);
      cleanupThumbnails(thumbnails);
    };
  }, [file]);

  const generateMainThumbnail = async () => {
    try {
      setIsGeneratingThumbnail(true);
      const thumbUrl = await generateVideoThumbnail(file, 1);
      setThumbnail(thumbUrl);
      onThumbnailGenerated?.(thumbUrl);
    } catch (error) {
      console.error('Failed to generate thumbnail:', error);
    } finally {
      setIsGeneratingThumbnail(false);
    }
  };

  const generateScrubThumbnails = async () => {
    try {
      const thumbUrls = await generateVideoThumbnails(file, 5);
      setThumbnails(thumbUrls);
    } catch (error) {
      console.error('Failed to generate scrub thumbnails:', error);
    }
  };

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleMuteToggle = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!videoRef.current) return;
    setDuration(videoRef.current.duration);
    generateScrubThumbnails();
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current || !duration) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * duration;
    
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleRestart = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = 0;
    setCurrentTime(0);
  };

  return (
    <div className={`relative bg-black rounded-lg overflow-hidden ${className}`}>
      {/* Thumbnail View */}
      {!showVideo && (
        <div className="relative aspect-[9/16] bg-gray-900 flex items-center justify-center">
          {isGeneratingThumbnail ? (
            <div className="flex flex-col items-center space-y-2 text-white">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              <p className="text-sm">Generating preview...</p>
            </div>
          ) : thumbnail ? (
            <>
              <img
                src={thumbnail}
                alt="Video thumbnail"
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => setShowVideo(true)}
                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-20 transition-all"
              >
                <div className="bg-white bg-opacity-90 rounded-full p-4 hover:bg-opacity-100 transition-all">
                  <Play className="w-8 h-8 text-black ml-1" />
                </div>
              </button>
            </>
          ) : (
            <div className="text-white text-center">
              <p className="text-sm">Failed to generate preview</p>
              <button
                onClick={generateMainThumbnail}
                className="mt-2 px-4 py-2 bg-blue-600 rounded-lg text-sm hover:bg-blue-700"
              >
                <RotateCcw className="w-4 h-4 inline mr-1" />
                Retry
              </button>
            </div>
          )}
        </div>
      )}

      {/* Video Player View */}
      {showVideo && (
        <div className="relative aspect-[9/16]">
          <video
            ref={videoRef}
            src={videoUrl}
            className="w-full h-full object-cover"
            muted={isMuted}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          />

          {/* Video Controls */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
            {/* Progress Bar */}
            <div 
              className="w-full h-2 bg-gray-600 rounded-full mb-3 cursor-pointer"
              onClick={handleSeek}
            >
              <div 
                className="h-full bg-white rounded-full transition-all"
                style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
              />
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handlePlayPause}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-all"
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </button>
                
                <button
                  onClick={handleMuteToggle}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-all"
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>

                <button
                  onClick={handleRestart}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-all"
                >
                  <RotateCcw className="w-5 h-5" />
                </button>
              </div>

              <div className="text-sm">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            </div>
          </div>

          {/* Back to Thumbnail Button */}
          <button
            onClick={() => setShowVideo(false)}
            className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm hover:bg-opacity-70 transition-all"
          >
            ← Preview
          </button>
        </div>
      )}

      {/* Thumbnail Scrubber */}
      {thumbnails.length > 0 && showVideo && (
        <div className="absolute bottom-16 left-4 right-4">
          <div className="flex space-x-1 bg-black bg-opacity-50 rounded-lg p-2">
            {thumbnails.map((thumb, index) => (
              <img
                key={index}
                src={thumb}
                alt={`Thumbnail ${index + 1}`}
                className="w-12 h-20 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => {
                  if (videoRef.current && duration) {
                    const newTime = ((index + 1) / (thumbnails.length + 1)) * duration;
                    videoRef.current.currentTime = newTime;
                  }
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPreview;
